import React, { useEffect, useRef, useState } from 'react';
import { useVideoPlayer } from '../hooks/useVideoPlayer';
import { styles } from './VideoPlayer.css';
import { PlayIllustrated, PauseIllustrated } from '@jbhi-fi/lanyard-ui/components/Icons';
import { Loading } from '@jbhi-fi/lanyard-ui/components/Icons';
import { VideoControls } from '../VideoControls/VideoControls';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

export type VideoPlayerProps = {
  src: string;
  type: string;
  width?: number | string;
  height?: number | string;
  shouldHideControls?: boolean;
  coverImage?: string | React.ReactElement;
  shouldAutoPlay?: boolean;
  onPlayPause?: (isNowPlaying: boolean) => void;
};

const CoverImage = ({ src }: { src: string | React.ReactElement }) => {
  if (typeof src === 'string') {
    return <img className={styles.cover} src={src} alt="cover" />;
  }

  return React.cloneElement(src, { className: styles.cover });
};

export const VideoPlayer = ({
  src,
  type,
  coverImage,
  width = '100%',
  height = '100%',
  shouldHideControls = false,
  shouldAutoPlay = false,
  onPlayPause,
}: VideoPlayerProps) => {
  const videoElement = useRef<HTMLVideoElement>(null);
  const [shouldShowControls, setShouldShowControls] = useState<boolean>(true);
  const [hasPlayed, setHasPlayed] = useState(false);
  const {
    canPlay,
    isPlaying,
    hasEnded,
    isMuted,
    togglePlay,
    handleRestart,
    handleOnTimeUpdate,
    handleRequestFullscreen,
    toggleMute,
    handleSeek,
    handleSeekStart,
    handleSeekEnd,
    progress,
    currentTime,
  } = useVideoPlayer(videoElement);

  const handlePlayPause = () => {
    if (!hasPlayed) setHasPlayed(true);
    togglePlay();
    onPlayPause?.(!isPlaying);
  };

  const handleShowControls = () => {
    setShouldShowControls(true);
  };

  const handleHideControls = (delay = 0) => {
    setTimeout(() => {
      setShouldShowControls(false);
    }, delay);
  };

  useEffect(() => {
    if (isPlaying) {
      handleHideControls(2500);
    }
  }, [isPlaying]);

  return (
    <div
      style={{ width, height }}
      className={styles.container}
      onMouseOver={handleShowControls}
      onFocus={handleShowControls}
      onMouseLeave={() => (hasPlayed ? handleHideControls() : null)}
    >
      {coverImage && !hasPlayed ? <CoverImage src={coverImage} /> : null}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoElement}
        width={width}
        height={height}
        onTimeUpdate={handleOnTimeUpdate}
        onEnded={handlePlayPause}
        autoPlay={shouldAutoPlay}
        playsInline
        muted={isMuted}
        className={styles.video}
      >
        <source src={src} type={type} />
        Your browser does not support the video tag.
      </video>
      {canPlay ? (
        <button
          className={styles.playButton({ isVisible: shouldShowControls })}
          onClick={hasEnded ? handleRestart : handlePlayPause}
          disabled={!canPlay}
        >
          {isPlaying ? <PauseIllustrated width={70} height={70} /> : <PlayIllustrated width={70} height={70} />}
        </button>
      ) : (
        <Loading className={styles.loadingIcon} width={50} height={50} color={palette.primary.main} />
      )}
      {!shouldHideControls && hasPlayed ? (
        <div className={styles.controls({ isVisible: shouldShowControls })}>
          <VideoControls
            isPlaying={isPlaying}
            isMuted={isMuted}
            progress={progress}
            currentTime={currentTime}
            onRequestFullscreen={handleRequestFullscreen}
            onSeek={handleSeek}
            onSeekStart={handleSeekStart}
            onSeekEnd={handleSeekEnd}
            onMute={toggleMute}
            onPlay={togglePlay}
          />
        </div>
      ) : null}
    </div>
  );
};
